// Keys and the pointer on the map, read through the scene's input:
// - keys: arrows/WASD walk (and bring the camera back to me), Enter opens chat, M mutes, +/- zoom, 1-8 actions (动作)
//   (/emotes.js), E sits on the seat I'm next to (or stands up); nothing while a text box has focus (钓鱼's F is
//   its own, only while 钓鱼 mode is open: public/fishing-mode.js);
//   a modifier key leaves the browser's shortcuts alone;
// - pointer, as in Gather: a single tap/click selects what's there (a person's card, an object's actions: public/pick.js),
//   a double one walks there (or to the gather flag, onto a seat, to a game table); press-and-drag pans the camera
//   (the otter stays put); a finger held still for LONG_PRESS_MS opens the 动作 menu (touch only; it doesn't walk);
//   on a name tag's icon that has a tooltip (ui.tipAt: 转写中) a held finger shows the tooltip instead, as hovering
//   the mouse over it does;
//   two fingers pinch to zoom; the wheel zooms around the cursor (a trackpad pinch is ctrl+wheel). Once panned, the
//   camera stays put (a click walks there, the view doesn't move) until I walk with the keys, walk near the window's
//   edge, press Space or 📍 回到我 (deck/camera.js: it glides back).
import { uiPointerDown, uiWheel, uiAt } from './ui/hit.js'; // the engine UI takes the pointer first
import { t } from '/i18n.js';

// A press only becomes a pan once it has moved this far: a click with a little mouse wobble (or a tap with a rolling
// finger) still walks.
const DRAG_PX = 12, DRAG_PX_TOUCH = 16, TAP_MS = 700, LONG_PRESS_MS = 550, TIP_TOUCH_MS = 3000;
// Two taps this close in time and place are a double tap / click (the second one walks).
const DOUBLE_MS = 400, DOUBLE_PX = 24, DOUBLE_PX_TOUCH = 36;
const WALK_KEYS = ['arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'w', 'a', 's', 'd'];
// Hovering the map with the mouse (as in Gather): corner brackets on the tile under the cursor (muted red where I
// can't walk), a thing that can be clicked lit up; held still HOVER_TIP_MS, a small dark tooltip says what a click and
// a double click do. A single click or tap on open deck or water says how to go there and ripples the tile, once no
// second click came. After a few walks by double click the passive floor-hover tooltip stops; an explicit click still
// shows the instruction.
const HOVER_TIP_MS = 400, HOVER_EVERY_MS = 120, HINT_MS = 1600, RIPPLE_MS = 500;
const WALKS_KEY = 'deck.walks2x', QUIET_FLOOR = 5;
let walks = 0; try { walks = Number(localStorage.getItem(WALKS_KEY)) || 0; } catch {}
function countWalk() { if (walks >= QUIET_FLOOR) return; walks++; try { localStorage.setItem(WALKS_KEY, String(walks)); } catch {} }

/** One small tooltip over the map (a DOM box at a screen point): show(text, x, y, ms?) (ms: hide by itself after), hide() */
function tooltip() {
  let el = null, timer = 0;
  return {
    show(text, x, y, ms) {
      if (!el) {
        el = document.createElement('div'); el.id = 'tagTip'; el.setAttribute('role', 'tooltip');
        el.style.cssText = 'position:fixed;z-index:60;pointer-events:none;background:#fff8e7;color:#1b1410;border:2px solid #1b1410;border-radius:8px;padding:3px 8px;font:12px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);white-space:nowrap;box-shadow:0 2px 0 rgba(27,20,16,.25)';
        document.body.append(el);
      }
      if (el.textContent !== text) el.textContent = text;
      el.style.display = '';
      const w = el.offsetWidth, h = el.offsetHeight; // above the point, kept on screen
      el.style.left = Math.round(Math.max(4, Math.min(innerWidth - w - 4, x - w / 2))) + 'px';
      el.style.top = Math.round(y - h - 14 < 4 ? y + 22 : y - h - 14) + 'px';
      clearTimeout(timer); if (ms) timer = setTimeout(() => this.hide(), ms);
    },
    hide() { clearTimeout(timer); if (el) el.style.display = 'none'; },
  };
}

/** The hover tooltip and the click hint: a small dark box (a DOM box at a screen point, above it). */
function darkTip() {
  let el = null, timer = 0, last = '';
  return {
    show(text, x, y, ms) {
      const sig = text + '|' + Math.round(x) + '|' + Math.round(y);
      if (!ms && sig === last && el?.style.display !== 'none') return; // each frame while it's up: nothing moved
      last = sig;
      if (!el) {
        el = document.createElement('div'); el.id = 'hoverTip'; el.setAttribute('role', 'tooltip');
        el.style.cssText = 'position:fixed;z-index:59;pointer-events:none;background:#1b1410;color:#fff8e7;border-radius:6px;padding:3px 8px;font:12px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);white-space:nowrap;box-shadow:0 2px 0 rgba(27,20,16,.3)';
        document.body.append(el);
      }
      if (el.textContent !== text) el.textContent = text;
      el.style.display = '';
      const w = el.offsetWidth, h = el.offsetHeight;
      el.style.left = Math.round(Math.max(4, Math.min(innerWidth - w - 4, x - w / 2))) + 'px';
      el.style.top = Math.round(y - h - 8 < 4 ? y + 24 : y - h - 8) + 'px';
      clearTimeout(timer); if (ms) timer = setTimeout(() => this.hide(), ms);
    },
    hide() { clearTimeout(timer); if (el) el.style.display = 'none'; },
  };
}

const typing = () => { const a = document.activeElement; return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable); };

/**
 * @param {object} o
 * @param {Phaser.Scene} o.scene the map's scene (deck/scene.js): keys, pointer and wheel come from its input
 * @param {HTMLCanvasElement} o.canvas
 * @param {object} o.camera deck/camera.js  @param {object} o.mover deck/move.js
 * @param {() => object|null} o.me
 * @param {object} o.ui chatOpen(), chatClose(), chatShown(), toggleMute(), rally(), goRally(), tableClick(tx, ty) (true: handled),
 *   emote(id) (keys 1-8), emoteMenu() (a long press),
 *   blockWalk() (true: the walking keys do nothing),
 *   interact() (E: sit / stand), seatClick(wx, wy) (a click on a seat; true: handled),
 *   tipAt(x, y) (the tooltip of a name tag icon at that screen point, or null),
 *   pick(wx, wy) (a single click at that world point: select what's there, returns it or null; without it a single click walks),
 *   walked() (a double click is walking me somewhere),
 *   hoverAt(wx, wy) (what the mouse is over: public/pick.js hover()),
 *   stick (deck/stick.js: down/move/up(id, x, y), true: a touch on the phone's stick, which keeps it till it lifts)
 * @returns {{hover: (now: number) => object|null}} for the scene, each frame: what to draw for the pointer (the
 *   hovered tile or thing, the click's ripple): {tile: {x, y, ok}} | {mark} (a pick.js mark), ripple: {x, y, a} (0..1)
 */
export function bindInput({ scene, canvas, camera, mover, me, ui }) {
  const keys = mover.keys;
  function keyDown(e) {
    if (typing() || e.metaKey || e.ctrlKey || e.altKey) return;
    const k = e.key.toLowerCase();
    if (k === 'escape' && ui.chatShown()) { e.preventDefault(); return ui.chatClose(); } // handled: 钓鱼 mode stays open
    if (k === 'enter') {
      // Enter on a button or link reached with Tab keeps doing what it does; a button that merely kept focus
      // after a mouse click (🎤, 聊天…) doesn't swallow it: Enter opens chat
      const a = document.activeElement;
      if (!me() || (['BUTTON', 'A', 'SELECT', 'SUMMARY'].includes(a?.tagName) && a.matches(':focus-visible'))) return;
      a?.blur?.();
      e.preventDefault(); return ui.chatOpen();
    }
    if (k === 'm') return ui.toggleMute();
    if (k === ' ') { if (camera.free) { e.preventDefault(); camera.follow(); } return; } // Space: back to me (回到我); in 按住说话 mode voice-settings.js takes it first
    if (k >= '1' && k <= '8' && k.length === 1) { if (!e.repeat) ui.emote?.(+k); return; }
    if (k === 'e') { if (!e.repeat) ui.interact?.(); return; } // sit down on the seat I'm next to / stand up (public/sit.js)
    if (k === '+' || k === '=') return camera.step(1);
    if (k === '-') return camera.step(-1);
    if (WALK_KEYS.includes(k) && ui.blockWalk?.()) return; // a panel covers the map (the 会议室 view): no walking blind
    if (WALK_KEYS.includes(k) && camera.free) camera.follow();
    keys.add(k); mover.clear();
  }
  const keyUp = (e) => keys.delete(e.key.toLowerCase());
  // Wheel over the map zooms around the cursor; over any panel it is left alone so the panel scrolls.
  // Trackpad pinch arrives as ctrl+wheel with small pixel deltas; plain two-finger scroll is gentler still.
  function wheel(e, x, y) {
    if (!me()) return;
    const dy = e.deltaY * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1);
    const f = Math.exp(-Math.max(-120, Math.min(120, dy)) / (e.ctrlKey || e.metaKey ? 200 : 300));
    camera.zoomAt(f, x, y);
  }
  const ptrs = new Map(); let tap = null, pinch = null; let editing = null, lastTap = null;
  const tip = tooltip();
  // the pointer's helpers (hover, the single click's hint): state, used by the listeners just below
  const dark = darkTip();
  let mouse = null; // {x, y, tag}: the mouse over the map, no button down (tag: over a name tag icon with its own tooltip)
  let hov = null; // {key, info, tx, ty, at (last asked), since (on this thing since), tipped}
  let muted = null; // the key that was under the mouse at the last press: no tooltip there until the mouse moves off it
  let ripple = null, hint = null; // ripple: {x, y, t}; hint: the single click waiting to see whether a second one comes
  let hintUntil = 0; // the single click's words are up until then (the hover tooltip leaves them alone)
  // A new action supersedes a pending single click, including a drag or a click on a panel outside the canvas.
  addEventListener('pointerdown', (e) => { clearTimeout(hint?.timer); hint = null; if (e.target !== canvas) lastTap = null; }, true);
  // Preserve the name-tag tooltip on the normal map; an edit stroke takes pointer input first.
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    const text = !e.buttons && !uiAt(scene, e.clientX, e.clientY) && ui.tipAt?.(e.clientX, e.clientY); // not over the engine UI
    if (text) tip.show(text, e.clientX, e.clientY); else tip.hide();
    mouse = e.buttons ? null : { x: e.clientX, y: e.clientY, tag: !!text };
  });
  canvas.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { tip.hide(); mouse = null; } });

  // ---- hover (mouse) and the single click's hint (both)
  const quiet = (n) => walks >= n;
  /** Can the pointer's helpers show now? Not while pressed, dragging, editing the map, or with the map covered. */
  const helpers = () => !!me() && !ptrs.size && editing === null && !ui.blockWalk?.();
  const hintUp = () => performance.now() < hintUntil;
  function unhover() { if (hov) { hov = null; if (!hintUp()) dark.hide(); if (!tap?.dragging) canvas.style.cursor = ''; } }
  function hover(now) {
    if (!mouse || !ui.hoverAt || !helpers()) { unhover(); return null; }
    const [wx, wy] = camera.toWorld(mouse.x, mouse.y), tx = Math.floor(wx), ty = Math.floor(wy);
    if (!hov || hov.tx !== tx || hov.ty !== ty || now - hov.at > HOVER_EVERY_MS) {
      // something over the map at the cursor (a panel, a game, a card that opened under a still mouse): nothing here
      if (document.elementFromPoint(mouse.x, mouse.y) !== canvas || uiAt(scene, mouse.x, mouse.y)) { unhover(); return null; }
      const info = ui.hoverAt(wx, wy);
      if (!info) { unhover(); return null; }
      if (hov?.key !== info.key) {
        if (info.key !== muted) muted = null;
        if (!hintUp()) dark.hide();
        hov = { key: info.key, since: muted ? Infinity : now, tipped: false };
      }
      const cur = info.kind === 'thing' ? 'pointer' : ''; if (canvas.style.cursor !== cur) canvas.style.cursor = cur; // each look (the engine UI may have reset it)
      Object.assign(hov, { info, tx, ty, at: now });
    }
    // a tile's words not while I'm walking (the tile under a still mouse keeps changing as the view follows me)
    const info = hov.info, text = info.kind === 'thing' ? info.tip : me().moving ? '' : info.kind === 'floor' ? (quiet(QUIET_FLOOR) ? '' : t('map.hint.walkHere')) : t('map.hint.blocked');
    if (hintUp()) { /* the single click's words stay up */ } else if (text && !mouse.tag && now - hov.since >= HOVER_TIP_MS) {
      // above the tile (it moves with the map), or above the cursor for a thing
      const S = camera.S(), at = info.tile ? [camera.x + (info.tile.x + 0.5) * S, camera.y + info.tile.y * S] : [mouse.x, mouse.y - 6];
      dark.show(text, at[0], at[1]); hov.tipped = true;
    } else if (hov.tipped) { dark.hide(); hov.tipped = false; }
    return info.tile ? { tile: { ...info.tile, ok: info.kind === 'floor' } } : { mark: info.mark?.() ?? null };
  }
  /** A single click or tap on floor or water: if no second one comes, say how to go there. */
  function emptyClick(sx, sy, touch, info, onSingle) {
    clearTimeout(hint?.timer);
    if (info?.kind !== 'floor' && !info?.sea) return;
    const [wx, wy] = camera.toWorld(sx, sy), tile = info.tile ?? { x: Math.floor(wx), y: Math.floor(wy) };
    hint = { timer: setTimeout(() => {
      hint = null; if (!helpers()) return;
      onSingle?.();
      ripple = { x: tile.x, y: tile.y, t: performance.now() };
      if (hov) { hov.since = Infinity; hov.tipped = false; }
      hintUntil = performance.now() + HINT_MS;
      dark.show(t(info.sea ? (touch ? 'map.hint.tapSwimHere' : 'map.hint.swimHere') : (touch ? 'map.hint.tapWalkHere' : 'map.hint.walkHere')), sx, sy - (touch ? 36 : 6), HINT_MS);
    }, DOUBLE_MS) };
  }
  function down(id, x, y, touch, timeStamp, shift) {
    if (touch && ui.stick?.down(id, x, y)) return; // the phone's stick (deck/stick.js): that finger is its own till it lifts
    ptrs.set(id, { x, y }); tip.hide(); muted = hov?.key ?? muted; unhover();
    if (hintUp()) { hintUntil = 0; dark.hide(); } // a new press: the last click's words go
    if (touch) mouse = null; // a touch screen with a mouse too: the mouse's spot is stale now
    if (ptrs.size === 1 && !shift) {
      const [wx, wy] = camera.toWorld(x, y);
      if (ui.mapEdit?.('down', Math.floor(wx), Math.floor(wy))) { editing = id; return; }
    }
    if (ptrs.size === 1) {
      tap = { id, x, y, t: timeStamp, touch, dragging: false, slop: touch ? DRAG_PX_TOUCH : DRAG_PX, dx: 0, dy: 0 };
      if (touch && (ui.emoteMenu || ui.tipAt)) { // held still: a name tag icon's tooltip, else the emote menu; this press no longer walks
        const t = tap; t.hold = setTimeout(() => {
          if (tap !== t || t.dragging || !me()) return;
          const text = ui.tipAt?.(t.x, t.y);
          if (text) { t.held = true; tip.show(text, t.x, t.y, TIP_TOUCH_MS); } else if (ui.emoteMenu) { t.held = true; ui.emoteMenu(); }
        }, LONG_PRESS_MS);
      }
    } else if (ptrs.size === 2) {
      lastTap = null;
      if(editing!==null){ui.mapEdit?.('cancel',0,0);editing=null;}
      clearTimeout(tap?.hold); tap = null; // a second finger: this is a pinch, not a tap or a pan
      const [a, b] = [...ptrs.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1 };
    }
  }
  function move(id, x, y) {
    if (ui.stick?.move(id, x, y)) return;
    if (!ptrs.has(id)) return;
    ptrs.set(id, { x, y });
    if (editing === id && ptrs.size === 1) { const [wx, wy] = camera.toWorld(x, y); ui.mapEdit?.('move', Math.floor(wx), Math.floor(wy)); return; }
    if (pinch && ptrs.size >= 2) {
      const [a, b] = [...ptrs.values()], d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      camera.zoom(d / pinch.d); pinch.d = d; return;
    }
    if (!tap || tap.id !== id) return;
    const dx = x - tap.x, dy = y - tap.y;
    if (!tap.dragging && !tap.held && Math.hypot(dx, dy) > tap.slop) { tap.dragging = true; clearTimeout(tap.hold); canvas.style.cursor = 'grabbing'; }
    // by how far the pointer moved since the last move, so past where the view may go (deck/camera.js), a drag back moves it at once
    if (tap.dragging) { const S = camera.S(); camera.dragBy((tap.dx - dx) / S, (tap.dy - dy) / S); tap.dx = dx; tap.dy = dy; }
  }
  function end(id, x, y, timeStamp, cancelled) {
    if (ui.stick?.up(id)) return;
    if (!ptrs.delete(id)) return;
    if (ptrs.size < 2) pinch = null;
    if (editing === id) { editing = null; ui.mapEdit?.('up', 0, 0); return; }
    const t = tap; if (!t || t.id !== id) return;
    tap = null; canvas.style.cursor = ''; clearTimeout(t.hold);
    if (!cancelled && !t.dragging && !t.held && timeStamp - t.t < TAP_MS) tapped(x, y, timeStamp, t.touch);
    else lastTap = null;
  }
  /** A tap or click that was neither a drag nor a long press: the second of two close together walks, else select. */
  function tapped(sx, sy, timeStamp, touch) {
    const l = lastTap;
    if (l && timeStamp - l.t < DOUBLE_MS && Math.hypot(sx - l.x, sy - l.y) < (touch ? DOUBLE_PX_TOUCH : DOUBLE_PX)) { lastTap = null; return mapClick(l.x, l.y, true); }
    lastTap = { x: sx, y: sy, t: timeStamp };
    if (!ui.pick) return mapClick(sx, sy);
    if (!me() || ui.blockWalk?.()) return;
    const [wx, wy] = camera.toWorld(sx, sy), info = ui.hoverAt?.(wx, wy);
    // A shore fishing card can cover the second tap. Open it only after this is known to be a single tap.
    if (info?.sea) emptyClick(sx, sy, touch, info, () => { if (ui.hoverAt?.(wx, wy)?.sea) ui.pick(wx, wy); });
    else { ui.pick(wx, wy); emptyClick(sx, sy, touch, info); }
  }
  function mapClick(sx, sy, double) {
    const m = me(); if (!m || ui.blockWalk?.()) return; // the view stays where it is (dragged or following): deck/camera.js
    const [wx, wy] = camera.toWorld(sx, sy), tx = Math.floor(wx), ty = Math.floor(wy);
    const rally = ui.rally();
    ui.walked?.();
    if (double) { clearTimeout(hint?.timer); hint = null; hintUntil = 0; dark.hide(); ripple = { x: tx, y: ty, t: performance.now() }; }
    if (rally && Math.abs(tx - rally.x) <= 1 && Math.abs(ty - rally.y) <= 2) return ui.goRally();
    if (ui.seatClick?.(wx, wy)) return; // a chair, bench, desk or meeting table seat: walk up and sit (public/sit.js)
    if (ui.tableClick?.(tx, ty)) return; // a game table: walk up and sit
    if (double) countWalk(); // repeated walks quiet the passive floor-hover hint
    mover.clickAt(wx, wy); // deck/move.js: finishes on the exact spot clicked; land from a boat means landing there
  }

  addEventListener('wheel', (e) => { if (e.target !== canvas && (e.ctrlKey || e.metaKey)) e.preventDefault(); }, { passive: false }); // no page zoom over panels either
  // keys from the window (never captured), the pointer and wheel from the canvas
  scene.input.keyboard.on('keydown', keyDown); scene.input.keyboard.on('keyup', keyUp);
  // Phaser's pointer is in canvas pixels, d (deck/dpr.ts) per CSS pixel; everything here and in deck/camera.js is CSS px.
  // The engine UI (deck/ui/hit.js) is asked first, with Phaser's own pointer (it converts it itself).
  const cx = (p) => p.x / scene.dpr, cy = (p) => p.y / scene.dpr;
  scene.input.on('wheel', (p) => { if (!uiWheel(scene, p)) wheel(p.event, cx(p), cy(p)); });
  // A drag that wanders over a bar or panel keeps panning: the canvas holds on to the pointer until it is released.
  canvas.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button === 0) canvas.setPointerCapture(e.pointerId); });
  scene.input.on('pointerdown', (p) => { if (uiPointerDown(scene, p)) { lastTap = null; return; } const e = p.event; if (e.type.startsWith('mouse') && e.button !== 0) return; down(p.id, cx(p), cy(p), p.wasTouch, e.timeStamp, e.shiftKey); });
  scene.input.on('pointermove', (p) => move(p.id, cx(p), cy(p)));
  const up = (p) => end(p.id, cx(p), cy(p), p.event.timeStamp, p.event.type.endsWith('cancel'));
  scene.input.on('pointerup', up); scene.input.on('pointerupoutside', up);
  return {
    hover(now) {
      const h = hover(now) ?? {};
      if (ripple) { const a = (now - ripple.t) / RIPPLE_MS; if (a >= 1 || a < 0) ripple = null; else h.ripple = { x: ripple.x, y: ripple.y, a }; }
      return h.tile || h.mark || h.ripple ? h : null;
    },
  };
}
